import { useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	FormControlLabel,
	Switch,
	Typography,
	Alert,
	CircularProgress,
	Collapse,
	List,
	ListItem,
	ListItemText
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/services';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export const CacheManagementCard = () => {
	const queryClient = useQueryClient();
	const [warmCaches, setWarmCaches] = useState(true);
	const [showDetails, setShowDetails] = useState(false);
	const [resetResult, setResetResult] = useState<{
		warmed_caches?: string[];
		warming_errors?: string[];
	} | null>(null);

	const { data: cacheStatus, isLoading: statusLoading } = useQuery({
		queryKey: ['admin', 'cache-status'],
		queryFn: adminApi.getCacheStatus,
		refetchInterval: 30000,
		staleTime: 30000
	});

	const resetMutation = useMutation({
		mutationFn: (warmCaches: boolean) => adminApi.resetCache(warmCaches),
		onSuccess: (data) => {
			setResetResult({
				warmed_caches: data.warmed_caches || [],
				warming_errors: data.warming_errors || []
			});
			queryClient.invalidateQueries({ queryKey: ['admin', 'cache-status'] });
		}
	});

	const handleReset = () => {
		if (window.confirm(
			warmCaches
				? 'Bạn có chắc muốn xóa và làm nóng lại tất cả cache?'
				: 'Bạn có chắc muốn xóa tất cả cache?'
		)) {
			setResetResult(null);
			resetMutation.mutate(warmCaches);
		}
	};

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Quản lý Cache
				</Typography>

				{statusLoading ? (
					<Box display="flex" justifyContent="center" p={2}>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ mt: 2 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
							<Typography variant="body1">
								Trạng thái Redis:
							</Typography>
							<Chip
								icon={cacheStatus?.redis_available ? <CheckCircleIcon /> : <ErrorIcon />}
								label={cacheStatus?.redis_available ? 'Khả dụng' : 'Không khả dụng'}
								color={cacheStatus?.redis_available ? 'success' : 'error'}
								size="small"
							/>
						</Box>

						<FormControlLabel
							control={
								<Switch
									checked={warmCaches}
									onChange={(e) => setWarmCaches(e.target.checked)}
									disabled={!cacheStatus?.redis_available || resetMutation.isPending}
								/>
							}
							label="Làm nóng cache sau khi xóa"
						/>

						<Box sx={{ mt: 2 }}>
							<Button
								variant="contained"
								color="warning"
								startIcon={resetMutation.isPending ? <CircularProgress size={20} /> : <RefreshIcon />}
								onClick={handleReset}
								disabled={!cacheStatus?.redis_available || resetMutation.isPending}
								fullWidth
								sx={{ minHeight: 40 }}
							>
								{resetMutation.isPending ? 'Đang xử lý...' : 'Xóa tất cả Cache'}
							</Button>
						</Box>

						{resetMutation.isSuccess && (
							<Alert severity="success" sx={{ mt: 2 }}>
								{warmCaches
									? 'Đã xóa và làm nóng lại cache thành công!'
									: 'Đã xóa tất cả cache thành công!'}
							</Alert>
						)}

						{resetMutation.isError && (
							<Alert severity="error" sx={{ mt: 2 }}>
								Lỗi khi xóa cache. Vui lòng thử lại.
							</Alert>
						)}

						{resetResult && warmCaches && (resetResult.warmed_caches || resetResult.warming_errors) && (
							<Box sx={{ mt: 2 }}>
								<Button
									size="small"
									onClick={() => setShowDetails(!showDetails)}
									endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								>
									{showDetails ? 'Ẩn chi tiết' : 'Xem chi tiết'}
								</Button>
								<Collapse in={showDetails}>
									<Box sx={{ mt: 2 }}>
										{resetResult.warmed_caches && resetResult.warmed_caches.length > 0 && (
											<Box sx={{ mb: 2 }}>
												<Typography variant="subtitle2" color="success.main" gutterBottom>
													Cache đã làm nóng ({resetResult.warmed_caches.length}):
												</Typography>
												<List dense>
													{resetResult.warmed_caches.slice(0, 10).map((cache, idx) => (
														<ListItem key={idx}>
															<ListItemText
																primary={cache}
																primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
															/>
														</ListItem>
													))}
													{resetResult.warmed_caches.length > 10 && (
														<ListItem>
															<ListItemText
																primary={`... và ${resetResult.warmed_caches.length - 10} cache khác`}
																primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
															/>
														</ListItem>
													)}
												</List>
											</Box>
										)}

										{resetResult.warming_errors && resetResult.warming_errors.length > 0 && (
											<Box>
												<Typography variant="subtitle2" color="error.main" gutterBottom>
													Lỗi khi làm nóng ({resetResult.warming_errors.length}):
												</Typography>
												<List dense>
													{resetResult.warming_errors.map((error, idx) => (
														<ListItem key={idx}>
															<ListItemText
																primary={error}
																primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
															/>
														</ListItem>
													))}
												</List>
											</Box>
										)}
									</Box>
								</Collapse>
							</Box>
						)}
					</Box>
				)}
			</CardContent>
		</Card>
	);
};
